import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';
import { BallTriangle } from 'react-loader-spinner';
import { io } from 'socket.io-client';
import jobServices from 'ApiService/Student/jobServices';
import ConfirmationModal from 'pages/Common/ConfirmationModal';
import CardCountdown from './CardCountdown';
import StudentAttachmentsView from './StudentAttachmentsView';
import JobsOverview from './JobsOverview';

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AssignedJobs = () => {
  const [jobs, setJobs] = useState([]);
  const { state } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showAcceptConfirmation, setShowAcceptConfirmation] = useState(false);

  const [showViewJobModal, setShowViewJobModal] = useState(false);
  const [pendingViewJobId, setPendingViewJobId] = useState(null);

  // Job response modals
  const [showWillAttendModal, setShowWillAttendModal] = useState(false);
  const [selectedJobForResponse, setSelectedJobForResponse] = useState(null);

  // Decline reason modal (used for both Cannot Attend & Decline Offer)
  const [showDeclineReasonModal, setShowDeclineReasonModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declineAction, setDeclineAction] = useState(null); // 'cannot-attend' | 'decline-offer'
  const [declineTargetId, setDeclineTargetId] = useState(null);

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobServices.getAssignedJobs();
      if (response.data.success) {
        const sortedJobs = [...response.data.assignedJobs].sort(
          (a, b) => new Date(b.assignedDate || b.createdAt) - new Date(a.assignedDate || a.createdAt)
        );
        setJobs(sortedJobs);
      } else {
        toast.error('No assigned jobs found.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch assigned jobs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (state?.user) {
      fetchJobs();
    }
  }, [state?.user, fetchJobs]);

  useEffect(() => {
    if (!state?.user?.id) return;
    const socketURL = process.env.REACT_APP_API_BASE_URL?.replace('/api/v1', '') || 'http://localhost:5001';
    const socket = io(socketURL, { withCredentials: true, transports: ['websocket'] });
    socket.on('connect', () => socket.emit('join', state.user.id));
    socket.on('NotifyUser', () => fetchJobs());
    return () => socket.disconnect();
  }, [state?.user?.id, fetchJobs]);

  const getCardStatus = (job) => {
    if (job.studentJobResponse === 'Cannot Attend')
      return { label: 'Declined', color: 'bg-red-100 text-red-800' };
    if (job.studentRevert === 'Accepted')
      return { label: 'Offer Accepted', color: 'bg-green-100 text-green-800' };
    if (job.studentRevert === 'Declined' || job.studentRevert === 'Auto Declined')
      return { label: 'Offer Declined', color: 'bg-red-100 text-red-800' };
    const roundOneStatus = job.rounds?.find((round) => round.roundNumber === 1)?.status;
    if (roundOneStatus === 'Shortlisted')
      return { label: 'Shortlisted', color: getStatusColor('Shortlisted') };
    if (roundOneStatus === 'Not Shortlisted')
      return { label: 'Not Shortlisted', color: getStatusColor('Not Shortlisted') };
    return { label: job.overallStatus, color: getStatusColor(job.overallStatus) };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Selected': return 'bg-green-100 text-green-800';
      case 'Shortlisted': return 'bg-green-100 text-green-800';
      case 'Not Selected':
      case 'Rejected': return 'bg-red-100 text-red-800';
      case 'Not Shortlisted': return 'bg-red-100 text-red-800';
      case 'In Progress':
      case 'Interview Scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'Assigned': return 'bg-blue-100 text-blue-800';
      case 'Declined':
      case 'Offer Declined':
      case 'Cannot Attend': return 'bg-red-100 text-red-800';
      case 'Offer Accepted': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleToggleExpand = (job) => {
    if (!job.viewedAt) {
      setPendingViewJobId(job._id);
      setShowViewJobModal(true);
      return;
    }
    setExpandedJobId(expandedJobId === job._id ? null : job._id);
  };

  const handleConfirmViewJob = async () => {
    if (submitting || !pendingViewJobId) return;
    try {
      setSubmitting(true);
      const response = await jobServices.markJobAsViewed(pendingViewJobId);
      const updatedJob = response?.data?.jobAssignment;
      setJobs((prev) =>
        prev.map((j) => (j._id === pendingViewJobId ? (updatedJob || { ...j, viewedAt: new Date().toISOString() }) : j))
      );
      setExpandedJobId(pendingViewJobId);
      setShowViewJobModal(false);
      setPendingViewJobId(null);
    } catch {
      toast.error('Failed to view job');
    } finally {
      setSubmitting(false);
    }
  };

  // Will Attend / Cannot Attend job response
  const handleWillAttendClick = (jobId) => {
    setSelectedJobForResponse(jobId);
    setShowWillAttendModal(true);
  };

  const handleWillAttend = async () => {
    if (submitting || !selectedJobForResponse) return;
    try {
      setSubmitting(true);
      const response = await jobServices.respondToJobAssignment(selectedJobForResponse, 'Will Attend');
      if (response.data.success) {
        toast.success('You confirmed you will attend');
        setShowWillAttendModal(false);
        setJobs((prev) =>
          prev.map((j) => (j._id === selectedJobForResponse ? { ...j, studentJobResponse: 'Will Attend' } : j))
        );
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to respond');
    } finally {
      setSubmitting(false);
      setSelectedJobForResponse(null);
    }
  };

  const handleCannotAttendClick = (jobId) => {
    setDeclineTargetId(jobId);
    setDeclineAction('cannot-attend');
    setDeclineReason('');
    setShowDeclineReasonModal(true);
  };

  const handleDeclineWithReason = async () => {
    if (submitting || !declineTargetId || !declineAction) return;
    try {
      setSubmitting(true);
      if (declineAction === 'cannot-attend') {
        const response = await jobServices.respondToJobAssignment(declineTargetId, 'Cannot Attend', declineReason);
        if (response.data.success) {
          toast.success('You declined this opportunity');
          setJobs((prev) =>
            prev.map((j) => (j._id === declineTargetId ? { ...j, studentJobResponse: 'Cannot Attend', revertReason: declineReason } : j))
          );
        }
      } else if (declineAction === 'decline-offer') {
        const response = await jobServices.studentRevertJobOffer(declineTargetId, 'Declined', null, declineReason);
        if (response.data.success) {
          toast.success('Offer declined');
          setJobs((prev) =>
            prev.map((j) => (j._id === declineTargetId ? { ...j, studentRevert: 'Declined', offerResendAllowed: false, revertReason: declineReason } : j))
          );
        }
      }
      setShowDeclineReasonModal(false);
      setDeclineReason('');
      setDeclineTargetId(null);
      setDeclineAction(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to decline');
    } finally {
      setSubmitting(false);
    }
  };

  // Offer accept/decline
  const handleAcceptOfferClick = (jobId) => {
    setSelectedJobId(jobId);
    setShowAcceptConfirmation(true);
  };

  const handleAcceptOffer = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      const response = await jobServices.studentRevertJobOffer(selectedJobId, 'Accepted');
      if (response.data.success) {
        toast.success('Offer accepted successfully');
        setShowAcceptConfirmation(false);
        setJobs((prev) =>
          prev.map((j) => (j._id === selectedJobId ? { ...j, studentRevert: 'Accepted', offerResendAllowed: false } : j))
        );
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Failed to accept job offer.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeclineOfferClick = (jobId) => {
    setDeclineTargetId(jobId);
    setDeclineAction('decline-offer');
    setDeclineReason('');
    setShowDeclineReasonModal(true);
  };

  const getRevertDisplay = (job) => {
    if (job.studentRevert === 'Auto Declined') return 'Auto Declined - Not taken response';
    if (job.studentRevert === 'Pending') return null;
    return job.studentRevert;
  };

  if (loading) {
    return <div className="flex h-40 items-center justify-center"><BallTriangle color="#5431ed" /></div>;
  }

  if (jobs.length === 0) {
    return <div className="flex h-40 items-center justify-center text-gray-500">No assigned jobs found.</div>;
  }

  return (
    <>
      <div className="mt-20 space-y-4 md:mt-10">
        <JobsOverview jobs={jobs} />
        {jobs.map((job) => {
          const isExpanded = expandedJobId === job._id;
          const showJobResponse = job.studentJobResponse === 'Pending' && job.viewedAt && job.overallStatus !== 'Selected' && job.overallStatus !== 'Rejected';
          const jobDeclined = job.studentJobResponse === 'Cannot Attend';

          return (
            <div key={job._id} className="relative z-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:bg-navy-800">
              {!job.viewedAt && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-sm">
                  <button onClick={() => handleToggleExpand(job)} className="rounded-lg bg-brand-500 px-6 py-3 text-base font-semibold text-white shadow-lg hover:bg-brand-600">View Job</button>
                </div>
              )}
              <div className={!job.viewedAt ? 'pointer-events-none select-none' : ''}>
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h2 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {!job.viewedAt ? 'New Job Assignment' : job.jobId ? job.jobId.jobTitle : job.companyId?.name || 'Company Assignment'}
                      </h2>
                      {job.viewedAt && (
                        <p className="text-sm text-gray-600">{job.jobId ? job.jobId.industry : job.roleId?.roleName || job.jobTitle || job.jdAttachment?.fileName || ''}</p>
                      )}
                      {job.viewedAt && !job.jobId && job.departmentId?.name && <p className="text-xs text-gray-500">Department: {job.departmentId.name}</p>}
                      {job.viewedAt && !job.jobId && job.clientId?.clientName && <p className="text-xs text-gray-500">Client: {job.clientId.clientName}</p>}
                    </div>
                    <span className={`inline-block shrink-0 rounded-lg px-2 py-1 text-sm font-medium ${getCardStatus(job).color}`}>{getCardStatus(job).label}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {job.jobId?.type && <span>{job.jobId.type}</span>}
                    {job.jobId?.location && <span>{job.jobId.location}</span>}
                    <span>Assigned: {new Date(job.assignedDate).toLocaleDateString()}</span>
                  </div>
                  <div className="mt-2">
                    <CardCountdown assignmentId={job._id} assignedDate={job.assignedDate} assignmentExpiry={job.assignmentExpiry} viewedAt={job.viewedAt} studentJobResponse={job.studentJobResponse} studentRevert={job.studentRevert} rounds={job.rounds} />
                  </div>
                  <div className="mt-3">
                    <button onClick={() => handleToggleExpand(job)} className="rounded bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
                      {isExpanded ? 'Hide Details' : 'View Job Info'}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t border-gray-200 p-4 dark:border-white/10">
                    {/* Job Details */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="font-medium text-gray-600">Location:</span> <span className="text-gray-800 dark:text-gray-200">{job.jobId?.location || (job.clientId ? job.clientId?.location : job.companyId?.location) || 'N/A'}</span></div>
                      <div><span className="font-medium text-gray-600">Type:</span> <span className="text-gray-800 dark:text-gray-200">{job.jobId?.type || 'Company Assignment'}</span></div>
                      {!job.jobId && job.departmentId?.name && <div><span className="font-medium text-gray-600">Department:</span> <span className="text-gray-800 dark:text-gray-200">{job.departmentId.name}</span></div>}
                      {!job.jobId && job.clientId?.clientName && <div><span className="font-medium text-gray-600">Client:</span> <span className="text-gray-800 dark:text-gray-200">{job.clientId.clientName}</span></div>}
                      <div><span className="font-medium text-gray-600">Assigned:</span> <span className="text-gray-800 dark:text-gray-200">{new Date(job.assignedDate).toLocaleDateString()}</span></div>
                      <div><span className="font-medium text-gray-600">Status:</span> <span className={`rounded-lg px-2 py-0.5 text-xs font-medium ${getCardStatus(job).color}`}>{getCardStatus(job).label}</span></div>
                      {job.notes && <div className="col-span-2"><span className="font-medium text-gray-600">Notes:</span> <span className="text-gray-800 dark:text-gray-200">{job.notes}</span></div>}
                    </div>

                    {/* Skills */}
                    {(job.jobId?.requiredSkills?.length > 0 || job.roleId?.skills?.length > 0) && (
                      <div className="mt-4">
                        <h3 className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">Skills</h3>
                        <div className="flex flex-wrap gap-1.5">
                          {(job.jobId?.requiredSkills || job.roleId?.skills || []).map((skill, i) => (
                            <span key={i} className="rounded-full bg-brand-100 px-2.5 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900 dark:text-brand-300">{skill}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Responsibilities */}
                    {job.jobId?.responsibilities?.length > 0 && (
                      <div className="mt-4">
                        <h3 className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">Responsibilities</h3>
                        <ul className="list-inside list-disc space-y-1 text-sm text-gray-600 dark:text-gray-400">
                          {job.jobId.responsibilities.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                      </div>
                    )}

                    {/* Description (for company assignments) */}
                    {!job.jobId && job.roleId?.description && (
                      <div className="mt-4">
                        <h3 className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">Description</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{job.roleId.description}</p>
                      </div>
                    )}

                    {/* Attached JD document */}
                    {job.jdAttachment?.filePath && (
                      <div className="mt-4">
                        <h3 className="mb-1 text-sm font-semibold text-gray-700 dark:text-gray-300">Job Description</h3>
                        <a
                          href={`${process.env.REACT_APP_API_BASE_URL}${job.jdAttachment.filePath}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-brand-600 hover:bg-gray-50 dark:border-white/10 dark:text-brand-400 dark:hover:bg-navy-700"
                        >
                          <span className="truncate">{job.jdAttachment.fileName}</span>
                          {job.jdAttachment.fileSize && (
                            <span className="shrink-0 text-xs text-gray-500">{formatFileSize(job.jdAttachment.fileSize)}</span>
                          )}
                          <span className="shrink-0 text-xs font-medium">Open</span>
                        </a>
                      </div>
                    )}

                    {/* Will Attend / Cannot Attend response */}
                    {showJobResponse && (
                      <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-navy-800">
                        <h3 className="mb-2 text-sm font-semibold text-blue-700 dark:text-blue-300">Job Response</h3>
                        <p className="mb-3 text-sm text-gray-600">Please confirm your availability for this opportunity.</p>
                        <div className="flex gap-2">
                          <button onClick={() => handleWillAttendClick(job._id)} disabled={submitting} className={`rounded bg-green-500 px-4 py-2 text-xs font-semibold text-white hover:bg-green-600 ${submitting ? 'cursor-not-allowed opacity-50' : ''}`}>Will Attend</button>
                          <button onClick={() => handleCannotAttendClick(job._id)} disabled={submitting} className={`rounded bg-red-500 px-4 py-2 text-xs font-semibold text-white hover:bg-red-600 ${submitting ? 'cursor-not-allowed opacity-50' : ''}`}>Cannot Attend</button>
                        </div>
                      </div>
                    )}

                    {/* Confirmed Will Attend (hide after HR schedules) */}
                    {job.studentJobResponse === 'Will Attend' && !jobDeclined && (!job.scheduledInterviews?.length) && (
                      <div className="mt-4 rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-700 dark:bg-navy-800">
                        <p className="text-sm font-medium text-green-700 dark:text-green-300">You confirmed you will attend. Waiting for HR to schedule an interview.</p>
                      </div>
                    )}

                    {/* Cancelled / Cannot Attend */}
                    {jobDeclined && (
                      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-navy-800">
                        <p className="text-sm font-medium text-red-700">You declined this job opportunity.</p>
                        {job.revertReason && <p className="mt-1 text-xs text-gray-600">Reason: {job.revertReason}</p>}
                      </div>
                    )}

                    {/* Scheduled Interviews */}
                    {job.scheduledInterviews?.length > 0 && !jobDeclined && (
                      <div className="mt-4">
                        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Interview Schedule</h3>
                        <div className="space-y-2">
                          {job.scheduledInterviews.map((interview, idx) => (
                            <div key={idx} className="rounded-lg border border-gray-200 p-3 text-sm dark:border-white/10">
                              <p><strong>Round {interview.roundNumber}:</strong> {new Date(interview.scheduledDate).toLocaleDateString()}{interview.scheduledTime ? ` at ${interview.scheduledTime}` : ''}</p>
                              {interview.notes && <p className="mt-1 text-xs text-gray-500">📌 {interview.notes}</p>}
                              {interview.status === 'Cancelled' && <p className="mt-1 font-medium text-red-600">Cancelled</p>}
                              {interview.status === 'Rescheduled' && <p className="mt-1 font-medium text-yellow-600">Rescheduled</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Interview Rounds (read-only) */}
                    {job.rounds?.length > 0 && !jobDeclined && (
                      <div className="mt-4">
                        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Interview Rounds</h3>
                        <div className="space-y-3">
                          {job.rounds.map((round, index) => (
                            <div key={index} className="flex flex-col space-y-2 rounded-lg border border-gray-200 p-3 dark:border-white/10">
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="text-sm font-medium dark:text-white">Round {round.roundNumber}</span>
                                  <span className="ml-2 text-xs text-gray-500">({new Date(round.date).toLocaleDateString()})</span>
                                </div>
                                <span className={`rounded-full px-2 py-1 text-xs font-medium ${
                                  round.status === 'Selected' || round.status === 'Selected for Next Round' || round.status === 'Shortlisted' ? 'bg-green-100 text-green-800' :
                                  round.status === 'Interview Scheduled' ? 'bg-blue-100 text-blue-800' :
                                  round.status === 'Rejected' || round.status === 'Not Selected' || round.status === 'Not Shortlisted' ? 'bg-red-100 text-red-800' :
                                  round.status === 'Not Attended' ? 'bg-orange-100 text-orange-800' :
                                  round.status === 'On Hold' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>{round.status}</span>
                              </div>
                              {round.feedback && <p className="text-xs text-gray-600">Feedback: {round.feedback}</p>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Offer Section */}
                    {job.overallStatus === 'Selected' && !jobDeclined && (
                      <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-navy-800">
                        <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Offer Section</h3>
                        {job.studentRevert === 'Accepted' ? (
                          <p className="text-sm font-medium text-green-600">You have accepted the offer letter.</p>
                        ) : job.studentRevert === 'Declined' || job.studentRevert === 'Auto Declined' ? (
                          <div>
                            <p className="text-sm font-medium text-red-600">Offer Declined</p>
                            {job.revertReason && <p className="mt-1 text-xs text-gray-500">Reason: {job.revertReason}</p>}
                          </div>
                        ) : (
                          job.viewedAt && (
                            <div className="mb-3 text-sm font-medium">
                              <OfferCountdown assignmentId={job._id} viewedAt={job.viewedAt} studentRevert={job.studentRevert} rounds={job.rounds} offerResendAllowed={job.offerResendAllowed} />
                            </div>
                          )
                        )}
                        {job.studentRevert !== 'Accepted' && (job.studentRevert === 'Pending' || job.offerResendAllowed) && !jobDeclined && (
                          <div className="flex flex-wrap gap-2">
                            {job.viewedAt && <span className="inline-flex items-center rounded bg-green-100 px-3 py-1 text-xs font-medium text-green-700">Viewed</span>}
                            <button onClick={() => handleAcceptOfferClick(job._id)} disabled={submitting} className={`rounded bg-green-500 px-4 py-2 text-xs font-semibold text-white hover:bg-green-600 ${submitting ? 'cursor-not-allowed opacity-50' : ''}`}>Accept Offer</button>
                            <button onClick={() => handleDeclineOfferClick(job._id)} disabled={submitting} className={`rounded bg-red-500 px-4 py-2 text-xs font-semibold text-white hover:bg-red-600 ${submitting ? 'cursor-not-allowed opacity-50' : ''}`}>Decline Offer</button>
                          </div>
                        )}
                        {job.offerResendAllowed && job.studentRevert === 'Pending' && <p className="mt-2 text-xs text-orange-600">Offer was resent by HR. You have 24 hours to respond.</p>}
                      </div>
                    )}

                    {/* Student Response */}
                    {getRevertDisplay(job) && (
                      <div className="mt-4 font-medium dark:text-white">
                        <span className="font-medium">Your response:</span>{' '}
                        <span className={job.studentRevert === 'Auto Declined' ? 'text-red-600' : ''}>{getRevertDisplay(job)}</span>
                      </div>
                    )}

                    {/* HR Shared Files */}
                    <div className="mt-4">
                      <StudentAttachmentsView assignmentId={job._id} />
                    </div>

                    {job.completedDate && <div className="mt-2 text-xs text-gray-500">Completed: {new Date(job.completedDate).toLocaleDateString()}</div>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      <ConfirmationModal isOpen={showViewJobModal} onClose={() => { setShowViewJobModal(false); setPendingViewJobId(null); }} onConfirm={handleConfirmViewJob}
        title={`${jobs.find(j => j._id === pendingViewJobId)?.assignmentExpiry || 24} Hour Review Period`} message={`You have ${jobs.find(j => j._id === pendingViewJobId)?.assignmentExpiry || 24} hours to review and respond to this job after viewing it. Do you want to proceed?`}
        confirmText={submitting ? 'Processing...' : 'Yes, I Understand'} icon="info" hideCancel />

      <ConfirmationModal isOpen={showWillAttendModal} onClose={() => !submitting && setShowWillAttendModal(false)} onConfirm={handleWillAttend}
        title="Confirm Attendance" message="Are you confirming you will attend this opportunity? HR will schedule an interview."
        confirmText={submitting ? 'Processing...' : 'Yes, I Will Attend'} cancelText="Cancel" icon="info" />

      <ConfirmationModal isOpen={showAcceptConfirmation} onClose={() => !submitting && setShowAcceptConfirmation(false)} onConfirm={handleAcceptOffer}
        title="Confirm Accept Offer" message="Are you sure you want to accept this job offer? This action cannot be undone."
        confirmText={submitting ? 'Processing...' : 'Yes, Accept Offer'} cancelText="Cancel" icon="info" />

      {/* Decline Reason Modal (Cannot Attend & Decline Offer) */}
      {showDeclineReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-navy-700">
            <h2 className="mb-3 text-lg font-semibold dark:text-white">
              {declineAction === 'cannot-attend' ? 'Decline Opportunity' : 'Decline Offer'}
            </h2>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              {declineAction === 'cannot-attend'
                ? 'Please provide a reason for declining this opportunity.'
                : 'Please provide a reason for declining this offer.'}
            </p>
            <textarea
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="Enter your reason..."
              rows={3}
              className="w-full rounded-lg border border-gray-300 p-3 text-sm focus:border-brand-500 focus:outline-none dark:bg-navy-800 dark:text-white"
            />
            <div className="mt-5 flex justify-end gap-3">
              <button
                onClick={() => { setShowDeclineReasonModal(false); setDeclineReason(''); setDeclineTargetId(null); setDeclineAction(null); }}
                disabled={submitting}
                className="rounded-full border-2 border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 dark:bg-navy-600 dark:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleDeclineWithReason}
                disabled={submitting || !declineReason.trim()}
                className="rounded-full bg-red-500 px-5 py-2 text-sm font-medium text-white hover:bg-red-600 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const OfferCountdown = ({ assignmentId, viewedAt, studentRevert, rounds, offerResendAllowed }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const autoDeclinedRef = useRef(false);

  const hasRoundResponse = rounds?.some((r) => r.studentStatus && r.studentStatus !== 'Pending');

  useEffect(() => {
    if (!viewedAt || hasRoundResponse || studentRevert !== 'Pending') return;
    const deadline = new Date(viewedAt).getTime() + 24 * 60 * 60 * 1000;
    const update = () => {
      const diff = deadline - Date.now();
      if (diff <= 0) {
        setTimeLeft('Deadline passed');
        setDeadlinePassed(true);
        if (!autoDeclinedRef.current && assignmentId) {
          autoDeclinedRef.current = true;
          jobServices.autoDeclineCheck(assignmentId).catch(() => {});
        }
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s left`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [viewedAt, studentRevert, hasRoundResponse, offerResendAllowed, assignmentId]);

  if (!timeLeft || studentRevert !== 'Pending') return null;

  const color = deadlinePassed ? 'text-red-600' : timeLeft.match(/(\d+)h/)?.[1] < 1 ? 'text-red-600 font-bold' : timeLeft.match(/(\d+)h/)?.[1] < 6 ? 'text-yellow-600 font-bold' : 'text-green-600';

  return <div className={color}>{deadlinePassed ? 'Deadline passed — auto-declined' : timeLeft}</div>;
};

export default AssignedJobs;
